import keys from '@debbie/common/dist/cache/keys.js';
import { InterestPeriodDefaultBehavior, } from '@debbie/common/dist/economy/case-vouchers/index.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { PaymentPlanStatus, isPreservedEntryStatus, replacementConfig, replacementDuration, replacementPreservedEntryIds, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { formatNumber } from '@debbie/common/dist/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { paymentPlans } from '@debbie/cortex/dist/api/index.js';
import { paymentPlans as paymentPlansApi } from '@debbie/cortex/dist/api/index.js';
import * as paymentPlansLib from '@debbie/cortex/dist/api/payment-plans.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import Button from '../Button.js';
import ExclusiveContent, { ExclusiveContentBadge } from '../ExclusiveContent.js';
import { Col, Row } from '../Grid/index.js';
import ArrowRight from '../Icons/ArrowRight.js';
import { File, Well } from '../index.js';
import EditPaymentPlanModal from './components/EditPaymentPlanModal.js';
import PaymentMethods from './components/PaymentMethods.js';
import PlanInfo from './components/PlanInfo.js';
import PlanOverview from './components/PlanOverview.js';
import SettlementProgress from './components/SettlementProgress.js';
import Logs from './PaymentPlanLogs.js';
const PaymentPlan = (props) => {
    const lang = useSelector((s) => s.lang);
    const tenant = useTenant();
    const documentsLoader = useDocumentsByReference(props.singleCase.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const creditorLoadable = useCreditor(props.singleCase.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(props.singleCase.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const conditionsLoader = useCaseConditions(props.singleCase.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const { caseVouchers, singleCase, paymentPlan } = props;
    const [editModalOpen, setEditModalOpen] = useState(false);
    const document = documents?.find((document) => document.documentId === paymentPlan.documentId);
    const file = document && (document.signedFile || document.file);
    const upcomingEntries = useMemo(() => {
        const firstReplaceableIdx = paymentPlan.plan.findIndex((e) => !isPreservedEntryStatus(e.status));
        return paymentPlan.plan.filter((entry, i) => i !== firstReplaceableIdx &&
            !isPreservedEntryStatus(entry.status) &&
            moment(entry.reminder).isAfter(moment()));
    }, [paymentPlan.plan]);
    const skipTo = upcomingEntries[0]?.date ||
        moment().add(1, 'month').startOf('month').format();
    const invalidatePaymentPlan = () => {
        q.invalidate(keys.write.modify(paymentPlansEntity, paymentPlan.paymentPlanId));
    };
    const deletePaymentPlanMutation = useMutation(() => paymentPlansLib.del(paymentPlan.paymentPlanId), {
        onSuccess: invalidatePaymentPlan,
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.paymentPlan);
        },
    });
    const activateMutation = useMutation(() => paymentPlans.patch(paymentPlan.paymentPlanId, {
        status: PaymentPlanStatus.ACTIVE,
    }), {
        onSuccess: invalidatePaymentPlan,
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.paymentPlan);
        },
    });
    const recalculateMutation = useMutation(() => paymentPlans.recalculate(paymentPlan.paymentPlanId), {
        onSuccess: invalidatePaymentPlan,
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.paymentPlan);
        },
    });
    const skipNextEntryMutation = useMutation(async () => {
        if (!paymentPlan.config) {
            throw new Error('Config missing');
        }
        let config = null;
        if (paymentPlan.config.method === 'duration') {
            config = replacementConfig(paymentPlan, {
                startDate: skipTo,
                method: 'duration',
                duration: replacementDuration(paymentPlan),
            });
        }
        else if (paymentPlan.config.method === 'amount' &&
            typeof paymentPlan.config.amount === 'number') {
            const futureScheduledChanges = (paymentPlan.config.scheduledAmountChanges ?? []).filter((c) => new Date(c.date).getTime() > new Date(skipTo).getTime());
            config = replacementConfig(paymentPlan, {
                startDate: skipTo,
                method: 'amount',
                amount: paymentPlan.config.amount,
                scheduledAmountChanges: futureScheduledChanges.length > 0
                    ? futureScheduledChanges
                    : undefined,
            });
        }
        if (config == null) {
            throw new Error('Payment plan config has neither duration nor amount method');
        }
        if (h.isError(conditionsLoader)) {
            throw new Error(lang.costCeiling.failedToLoad);
        }
        if (tenant == null ||
            creditor == null ||
            documents == null ||
            fixedDistributions == null ||
            conditions == null) {
            throw new Error('Required data is still loading');
        }
        const plan = await helpers.paymentPlans.genReplacement(tenant, creditor, props.singleCase.economyEngine, JSON.parse(JSON.stringify(helpers.vouchers.postProcessing(caseVouchers, {
            includeIrrecoverableVouchers: false,
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
        }))), documents, fixedDistributions, paymentPlan, config, conditions);
        await paymentPlansApi.create(paymentPlan.caseId, {
            activationTrigger: false,
            plan: plan.map((entry) => ({
                amount: entry.amount,
                cancel: entry.cancel,
                deadline: entry.deadline,
                reminder: entry.reminder,
                date: entry.date,
                type: entry.type,
            })),
            config,
            preservedEntryIds: replacementPreservedEntryIds(paymentPlan),
        });
    }, {
        onSuccess: () => {
            q.invalidate(keys.write.create(paymentPlansEntity));
        },
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.paymentPlan);
        },
    });
    return (React.createElement(Well, { style: { margin: '20px 0' } },
        React.createElement(Row, null,
            React.createElement(Col, { width: "12" },
                React.createElement(ConfigHeader, null,
                    React.createElement("h4", { style: { margin: 0 } }, lang.paymentPlans.paymentPlan),
                    React.createElement(Logs, { paymentPlanId: props.paymentPlan.paymentPlanId, paymentPlanEntryId: null }))),
            React.createElement(Col, { width: "6" }, paymentPlan.plan && (React.createElement(ConfigContainer, null,
                React.createElement(PlanInfo, { paymentPlan: paymentPlan, singleCase: singleCase }),
                React.createElement(SettlementProgress, { paymentPlan: paymentPlan, caseVouchers: caseVouchers, currency: singleCase?.currency }),
                React.createElement(PlanOverview, { paymentPlan: paymentPlan, singleCase: singleCase }),
                paymentPlan.config?.interestFree && (React.createElement(InfoRow, null,
                    React.createElement(InfoLabel, null, lang.paymentPlans.interestFree),
                    React.createElement(InfoValue, null,
                        moment(paymentPlan.config.interestFree.from).format(THEME.DATE_FORMAT_SHORT),
                        paymentPlan.config.interestFree.to && (React.createElement(React.Fragment, null,
                            React.createElement(ArrowRight, null),
                            moment(paymentPlan.config.interestFree.to).format(THEME.DATE_FORMAT_SHORT)))))),
                paymentPlan.config?.interestFree?.onDefault != null && (React.createElement(InfoRow, null,
                    React.createElement(InfoLabel, null, lang.paymentPlans.interestFreeOnDefault),
                    React.createElement(InfoValue, null, paymentPlan.config.interestFree.onDefault ===
                        InterestPeriodDefaultBehavior.Reapply
                        ? lang.paymentPlans.interestFreeOnDefaultReapply
                        : lang.paymentPlans.interestFreeOnDefaultKeepFrozen))),
                (paymentPlan.config?.feeStructure?.initialFee ||
                    paymentPlan.config?.feeStructure?.perEntryFee) && (React.createElement(Subsection, null,
                    React.createElement(SubsectionHeader, null, lang.paymentPlans.fees),
                    paymentPlan.config.feeStructure?.initialFee && (React.createElement(InfoRow, { $compact: true },
                        React.createElement(InfoLabel, null, lang.paymentPlans.initialFee),
                        React.createElement(InfoValue, null,
                            formatNumber(paymentPlan.config.feeStructure.initialFee.amount),
                            ' ',
                            React.createElement(Currency, null, singleCase?.currency)))),
                    paymentPlan.config.feeStructure?.perEntryFee && (React.createElement(InfoRow, { $compact: true },
                        React.createElement(InfoLabel, null, lang.paymentPlans.perEntryFee),
                        React.createElement(InfoValue, null,
                            formatNumber(paymentPlan.config.feeStructure.perEntryFee.amount),
                            ' ',
                            React.createElement(Currency, null, singleCase?.currency))))))))),
            React.createElement(Col, { width: "6" },
                React.createElement(PaymentMethods, { paymentPlan: paymentPlan, singleCase: singleCase }),
                file && (React.createElement("div", { style: { marginBottom: '12px' } },
                    React.createElement(Subsection, null,
                        React.createElement(SubsectionHeader, null, lang.paymentPlans.agreement),
                        React.createElement("div", null,
                            React.createElement(File, { file: file }))))),
                React.createElement(ActionsStack, null,
                    paymentPlan.status === PaymentPlanStatus.CREATED && (React.createElement(Subsection, null,
                        React.createElement(SubsectionHeader, null, lang.paymentPlans.activate),
                        React.createElement(ActionDescription, null, lang.paymentPlans.activatePaymentPlanWithoutFoundation),
                        React.createElement(ActionFooter, null,
                            React.createElement(Button, { size: "mini", theme: "light", loading: activateMutation.isLoading, onClick: () => activateMutation.mutate() }, lang.paymentPlans.activate)))),
                    [PaymentPlanStatus.ACTIVE, PaymentPlanStatus.CANCELED].includes(paymentPlan.status) &&
                        paymentPlan.config && (React.createElement(Subsection, null,
                        React.createElement(SubsectionHeader, null, lang.paymentPlans.editPaymentPlan),
                        React.createElement(ActionDescription, null, lang.paymentPlans.editPaymentPlanDescription),
                        React.createElement(ActionFooter, null,
                            [
                                PaymentPlanStatus.ACTIVE,
                                PaymentPlanStatus.CANCELED,
                            ].includes(paymentPlan.status) &&
                                paymentPlan.config && (React.createElement(Button, { size: "mini", onClick: () => skipNextEntryMutation.mutate(), loading: skipNextEntryMutation.isLoading, style: { marginRight: '5px' } },
                                lang.paymentPlans.skipTo,
                                ' ',
                                moment(skipTo).format('DD/MM'))),
                            React.createElement(Button, { size: "mini", onClick: () => setEditModalOpen(true) }, lang.paymentPlans.editPaymentPlan)))),
                    [PaymentPlanStatus.CREATED, PaymentPlanStatus.ACTIVE].includes(paymentPlan.status) && (React.createElement(ExclusiveContent, null,
                        React.createElement(Subsection, null,
                            React.createElement(SubsectionHeaderRow, null,
                                React.createElement(SubsectionHeader, null, lang.paymentPlans.recalculate),
                                React.createElement(ExclusiveContentBadge, null)),
                            React.createElement(ActionDescription, null, lang.paymentPlans.recalculateDescription),
                            React.createElement(ActionFooter, null,
                                React.createElement(Button, { onClick: () => recalculateMutation.mutate(), size: "small", theme: "light", loading: recalculateMutation.isLoading }, lang.paymentPlans.recalculate))))),
                    [PaymentPlanStatus.CREATED, PaymentPlanStatus.ACTIVE].includes(paymentPlan.status) && (React.createElement(DangerZone, null,
                        React.createElement(Button, { onClick: () => deletePaymentPlanMutation.mutate(), size: "mini", theme: "dangerous", loading: deletePaymentPlanMutation.isLoading }, lang.paymentPlans.deletePaymentPlan)))))),
        editModalOpen && (React.createElement(EditPaymentPlanModal, { paymentPlan: paymentPlan, caseVouchers: caseVouchers, singleCase: singleCase, onClose: () => setEditModalOpen(false) }))));
};
export default PaymentPlan;
const ConfigContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ConfigHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1em;
`;
const Currency = styled.span `
  font-size: 12px;
  color: #888;
  font-weight: 500;
`;
const Subsection = styled.div `
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const SubsectionHeader = styled.h5 `
  margin: 0 0 4px 0;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #555;
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: ${({ $compact }) => ($compact ? '4px 0' : '8px 0')};
  border-bottom: ${({ $compact }) => ($compact ? 'none' : '1px solid #f0f0f0')};

  &:last-child {
    border-bottom: none;
  }
`;
const InfoLabel = styled.span `
  font-size: 13px;
  color: #666;
`;
const InfoValue = styled.span `
  font-size: 14px;
  color: #222;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
const ActionsStack = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const ActionFooter = styled.div `
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;
const DangerZone = styled.div `
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  margin-top: 4px;
  border-top: 1px solid #f0f0f0;
`;
const SubsectionHeaderRow = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
const ActionDescription = styled.p `
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.4;
`;
//# sourceMappingURL=PaymentPlan.js.map